import Clouds from '@/components/shared/Clouds';
import { MainHeader, Section, SectionParagraph } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import { PartnerLogo } from '@/components/shared/PartnerLogo';
import { useGetAuthUser } from '@/hooks/useGetAuthUser';
import { useShowHeaderCtaOnScroll } from '@/hooks/useShowHeaderCtaOnScroll';
import pressBanner from '@/static/assets/home/press_banner_without_inc.svg';
import { Box, Flex, font, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { useAnalyticsContext, useI18nContext } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import { motion } from 'motion/react';
import dynamic from 'next/dynamic';
import type React from 'react';
import { useMemo } from 'react';
import { scroller } from 'react-scroll';
import styled from 'styled-components';
// banners
const PressBanner = dynamic(() => import('@/components/home/PressBanner').then(mod => mod.PressBanner));
const PressBannerLogos = dynamic(() => import('@/components/home/PressBannerLogos'));
// heros
const DefaultHero = dynamic(() => import('@/components/home/DefaultHero').then(mod => mod.DefaultHero));
const FrHero = dynamic(() => import('@/components/home/FrHero').then(mod => mod.FrHero));

const HERO_COMPONENTS_TYPE = {
  defaultHero: DefaultHero,
  frHero: FrHero,
} as const;

const TopFoldSection = styled(Section)<{ withSecondaryCta: boolean }>`
  position: relative;
  height: 617px;
  z-index: 1;
  background-color: transparent;

  &:before,
  &:after {
    z-index: -1;
  }

  ${MEDIA_QUERIES.mobile<{ withSecondaryCta: boolean }>`
        background: linear-gradient(to top, ${themedColor('cloud')} 27px,
            transparent 27px);
        height: ${({ withSecondaryCta }) => (withSecondaryCta ? '675px' : '617px')};
    `}
`;

const StyledClouds = styled(motion.create(Clouds))`
  transition: opacity 1s ease-in-out;
`;

const TopFoldTitle = styled(MainHeader)`
  white-space: initial;
  ${MEDIA_QUERIES.desktop`
    white-space: pre;
  `}
`;

const MobileBackToQuote = styled.a`
  display: none;

  ${MEDIA_QUERIES.mobile`
        display: flex;
        color: ${themedColor('primary')};
        ${font('main', {
          fontSize: '14px',
          lineHeight: 'normal',
          fontWeight: 400,
        })};
        letter-spacing: normal;
        padding: 9px 18px 12px 18px;
        box-shadow: 0 47px 46px -27px rgba(0, 0, 0, 0.16), 0 2px 12px rgba(0, 0, 0, 0.06);
        border-radius: 100px;
        margin-bottom: 19px;
        width: 180px;
        height: 38px;
        background: transparent url("/pink-right-arrow.svg") no-repeat;
        background-position: 148px 11px;
        -webkit-tap-highlight-color: transparent;
        margin-top: -20px;

        html[lang='de-DE'] & {
            width: 230px;
            background: transparent url("/arrow_right_red.svg") no-repeat;
            background-size: 15px 15px;
            background-position: 197px 11px;
        }

        html[lang='en-DE'] & {
            background: transparent url("/arrow_right_red.svg") no-repeat;
            background-size: 15px 15px;
            background-position: 148px 11px;
        }
    `};
`;

const Actions = styled(Flex)`
  z-index: 1;
`;

const CTA = styled(JoinCTA)`
  &&& {
    display: block;
    padding: 18px 44px;
    font-size: 1.8rem;
  }
`;

const SecondaryCta = styled.a`
  margin-top: 10px;
  color: ${themedColor('darkGray')};
  ${font('main', { fontSize: '14px', lineHeight: '28px' })};
  text-align: center;
  cursor: pointer;
  ${MEDIA_QUERIES.mobile`
    width: 300px;
  `}
`;

const BANNERS = {
  withoutLogos: <PressBanner img={pressBanner} />,
  withLogos: <PressBannerLogos />,
} as Record<Exclude<HeroProps['includePressBanner'], undefined>, React.ReactNode>;

export interface HeroProps extends Omit<React.ComponentProps<'div'>, 'title'> {
  readonly secondaryCta?: {
    label: string;
    scrollToSection: string;
  };
  readonly title: string;
  readonly subtitle: string;
  readonly ctaLabel: string;
  readonly ctaUrl?: string;
  readonly cart: {
    readonly singular: string;
    readonly plural: string;
  };
  readonly includePressBanner?: 'withLogos' | 'withoutLogos';
  readonly partnerLogo?: string;
  readonly heroType?: keyof typeof HERO_COMPONENTS_TYPE;
  readonly assignRefToMainCta?: boolean;
}

const MainCta: React.FC<Pick<HeroProps, 'ctaLabel' | 'ctaUrl' | 'assignRefToMainCta'>> = ({
  ctaLabel,
  ctaUrl,
  assignRefToMainCta = true,
}) => {
  const { ref: mainCtaRef } = useShowHeaderCtaOnScroll();
  return (
    <Box mt={[30, 45, 75]}>
      <CTA
        ref={assignRefToMainCta ? mainCtaRef : undefined}
        flavor="primary"
        glowing
        aria-label={ctaLabel}
        id="gtm_button_main_check_prices"
        data-source="top"
        customUrl={ctaUrl}
      >
        {ctaLabel}
      </CTA>
    </Box>
  );
};

export const Hero: React.FC<HeroProps> = ({
  secondaryCta,
  title,
  subtitle,
  ctaLabel,
  cart,
  ctaUrl,
  includePressBanner = 'withoutLogos',
  partnerLogo,
  heroType = 'defaultHero',
  assignRefToMainCta = true,
}) => {
  const { user } = useGetAuthUser();
  const { locale } = useI18nContext();

  const { trackEvent } = useAnalyticsContext();

  const HeroType = useMemo(() => HERO_COMPONENTS_TYPE[heroType], [heroType]);

  const showQuotePath = user?.showCart && user?.quotePath != null && user?.userFirstName != null;

  const scrollToSection = (place: string): void => {
    scroller.scrollTo(place, {
      duration: 1100,
      smooth: true,
      offset: -30,
    });
    trackEvent(`switching_scroll_link_${locale}_clicked`);
  };

  const onSecondaryCtaPressed = (e: React.KeyboardEvent<HTMLAnchorElement>): void => {
    if (secondaryCta == null) return;
    if (e.key === 'Enter') {
      scrollToSection(secondaryCta.scrollToSection);
      document.getElementById('switching-eu-title')?.focus();
    }
  };

  const onSecondaryCtaClicked = (): void => {
    if (secondaryCta == null) return;

    scrollToSection(secondaryCta.scrollToSection);
    document.getElementById('switching-eu-title')?.focus();
  };

  return (
    <>
      <PartnerLogo src={partnerLogo} aria-hidden={true} />
      <TopFoldSection px="20px" pt={[15, 27, 80]} pb={[140, 201, 266]} withSecondaryCta={secondaryCta != null}>
        {showQuotePath && <MobileBackToQuote href={user.quotePath}>{cart.plural}</MobileBackToQuote>}
        {user?.showCart && (
          <MobileBackToQuote href={user?.cartPath}>
            {(user?.cartCount ?? 0) > 1 ? cart.plural : cart.singular}
          </MobileBackToQuote>
        )}
        <Flex gap="20px" flexDirection="column" justifyContent="center">
          <TopFoldTitle tabIndex={0}>{parse(title)}</TopFoldTitle>
          <SectionParagraph tabIndex={0}>{parse(subtitle)}</SectionParagraph>
        </Flex>
        <Actions flexDirection="column" alignItems="center">
          <MainCta ctaLabel={ctaLabel} ctaUrl={ctaUrl} assignRefToMainCta={assignRefToMainCta} />
          {secondaryCta && (
            <SecondaryCta tabIndex={0} onClick={onSecondaryCtaClicked} onKeyDown={onSecondaryCtaPressed}>
              {secondaryCta.label}
            </SecondaryCta>
          )}
        </Actions>
        <StyledClouds top={['45%', '20%', '23%']} animate={{ opacity: [0, 1] }} />
        {/* dynamic heros types here */}
        <HeroType />
      </TopFoldSection>

      <Box display={['none', 'block']}>{BANNERS[includePressBanner]}</Box>
    </>
  );
};
